// Документы: договор, счёт, акт, проверка.
//
// ЧЕМ ОНИ ОТЛИЧАЮТСЯ ОТ СПРАВОЧНИКОВ. У документа есть состояние `draft/issued/cancelled`
// и номер, а номер выдаётся ровно в одном месте — при переходе из черновика в выпущенный
// (`cir-core/МОДЕЛЬ_ДАННЫХ.md` §5.1). Отсюда условная обязательность: у черновика номера
// нет и взять его неоткуда, у выпущенного он обязан быть. Проверяется это функцией
// `выпущен` — она читает состояние из значений, см. `obshchee.ts`.
//
// ЗАЧЕМ ПОЧИНКА НУМЕРАЦИИ ВООБЩЕ. Сегодня номер — свободный текст, который вписывают
// руками, и уникальным он давно быть перестал: у актов один и тот же номер встречается
// по нескольку раз, а среди номеров счетов попадаются «б/н» и обрывки вроде запятой с
// цифрой. Нумератор — не украшение, а починка (§5.1).
//
// ОПЛАТА СЧЁТА — ОДИН ПРИЗНАК. Факт оплаты — это заполненная дата оплаты, и только она.
// Сегодня «оплачено» проверяется двумя способами сразу, датой и галочкой, и они расходятся
// (§5.2). Галочки «оплачен» здесь нет нарочно.

import { fmtDate, fmtMoney, todayIso } from '../../lib/format'
import { деньги, число, пусто, строка, ярлык } from '../values'
import type { ОписаниеКарточки, Значения } from '../types'

import {
  ВИДЫ_ДОГОВОРА,
  ВИДЫ_ПРОВЕРКИ,
  выпущен,
  илиПрочерк,
  НАПРАВЛЕНИЯ_СЧЁТА,
  подписьВарианта,
  РЕЗУЛЬТАТЫ_ПРОВЕРКИ,
  СТАВКИ_НДС,
  СТАТЬИ_РАСХОДОВ,
} from './obshchee'

/** Срок в прошлом. Пустая дата сроком не считается: незаполненное — не просрочка. */
const срокПрошёл = (з: Значения, код: string): boolean => {
  const д = строка(з, код)
  return д !== '' && д < todayIso()
}

const суммаТекстом = (з: Значения, код: string): string => {
  const д = деньги(з, код)
  return д.сумма === null ? '—' : fmtMoney(д.сумма, д.валюта)
}

// ─────────────────────────── договор ───────────────────────────

export const ДОГОВОР: ОписаниеКарточки = {
  сущность: 'договор',
  один: 'договор',
  винительный: 'договор',
  много: 'Договоры',
  документ: true,
  путь: (id) => `/cards/contract/${id}`,
  заголовок: (з) => {
    const н = строка(з, 'no')
    return н === '' ? 'Договор без номера (черновик)' : `Договор № ${н}`
  },

  показатели: [
    { код: 'номер', подпись: 'Номер', значение: (з) => илиПрочерк(строка(з, 'no')) },
    {
      код: 'вид',
      подпись: 'Вид',
      значение: (з) => подписьВарианта(ВИДЫ_ДОГОВОРА, з, 'kind'),
    },
    {
      код: 'до',
      подпись: 'Действует до',
      значение: (з) => (пусто(з.valid_to) ? 'Бессрочно' : fmtDate(строка(з, 'valid_to'))),
      тревога: (з) => срокПрошёл(з, 'valid_to'),
    },
  ],

  группы: [
    { код: 'номер', заголовок: 'Номер и вид', вкладка: 'основное' },
    {
      код: 'стороны',
      заголовок: 'Стороны',
      вкладка: 'основное',
      подсказка: 'Кто с кем. Наше юрлицо отсюда подставляется в сделки по этому договору.',
    },
    { код: 'сроки', заголовок: 'Сроки', вкладка: 'основное' },
    { код: 'связи', заголовок: 'Чем связан', вкладка: 'связи' },
  ],

  поля: [
    {
      код: 'no',
      подпись: 'Номер',
      вид: 'строка',
      группа: 'номер',
      обязательно: выпущен,
      почемуОбязательно: 'выпущенный договор без номера не найти ни в счёте, ни в акте; у черновика номера нет вовсе — он выдаётся при выпуске',
      максДлина: 64,
    },
    {
      код: 'kind',
      подпись: 'Вид договора',
      вид: 'список',
      варианты: ВИДЫ_ДОГОВОРА,
      группа: 'номер',
      обязательно: true,
      почемуОбязательно: 'от вида зависит, можно ли цеплять к договору приложения',
    },
    {
      код: 'parent_id',
      подпись: 'Рамочный договор',
      вид: 'связь',
      связьС: 'договор',
      группа: 'номер',
      подсказка: 'Заполняется только у приложения. Приложение наследует стороны рамочного — менять их в приложении нельзя.',
    },
    {
      код: 'company_id',
      подпись: 'Контрагент',
      вид: 'связь',
      связьС: 'компания',
      группа: 'стороны',
      обязательно: true,
      почемуОбязательно: 'договор всегда с кем-то, и по этой ссылке он виден в карточке контрагента',
    },
    {
      код: 'own_company_id',
      подпись: 'Наша компания',
      вид: 'связь',
      связьС: 'компания',
      группа: 'стороны',
      обязательно: true,
      почемуОбязательно: 'от кого мы подписываем — это же юрлицо уедет в счета по договору',
    },
    {
      код: 'signed_at',
      подпись: 'Подписан',
      вид: 'дата',
      группа: 'сроки',
      подсказка: 'День подписи оригинала, а не день, когда скан дошёл до нас.',
    },
    { код: 'valid_from', подпись: 'Действует с', вид: 'дата', группа: 'сроки' },
    {
      код: 'valid_to',
      подпись: 'Действует до',
      вид: 'дата',
      группа: 'сроки',
      подсказка: 'Пусто — бессрочный. Дата в прошлом подсвечивается: по истёкшему договору счета выставлять нельзя.',
    },
    {
      код: 'files',
      подпись: 'Сканы договора',
      вид: 'файл',
      группа: 'связи',
    },
  ],

  обратные: [
    {
      код: 'счета',
      заголовок: 'Счета по договору',
      сущность: 'счёт',
      поле: 'contract_id',
      колонки: [
        { код: 'no', подпись: 'Номер' },
        { код: 'issued_at', подпись: 'Выставлен' },
        { код: 'amount', подпись: 'Сумма', число: true },
        { код: 'paid_at', подпись: 'Оплачен' },
      ],
    },
    {
      код: 'сделки',
      заголовок: 'Сделки по договору',
      сущность: 'сделка',
      поле: 'contract_id',
      колонки: [
        { код: 'title', подпись: 'Название' },
        { код: 'stage_id', подпись: 'Стадия' },
        { код: 'amount', подпись: 'Сумма', число: true },
      ],
    },
  ],
}

// ─────────────────────────── счёт ───────────────────────────

export const СЧЁТ: ОписаниеКарточки = {
  сущность: 'счёт',
  один: 'счёт',
  винительный: 'счёт',
  много: 'Счета',
  документ: true,
  путь: (id) => `/cards/invoice/${id}`,
  заголовок: (з) => {
    const н = строка(з, 'no')
    return н === '' ? 'Счёт без номера (черновик)' : `Счёт № ${н}`
  },

  показатели: [
    { код: 'сумма', подпись: 'Сумма', значение: (з) => суммаТекстом(з, 'amount') },
    {
      код: 'оплата',
      подпись: 'Оплата',
      значение: (з) => (пусто(з.paid_at) ? 'Не оплачен' : `Оплачен ${fmtDate(строка(з, 'paid_at'))}`),
      тревога: (з) => пусто(з.paid_at),
    },
    {
      код: 'просрочка',
      подпись: 'Срок оплаты',
      значение: (з) => (пусто(з.due_at) ? '—' : fmtDate(строка(з, 'due_at'))),
      // Просрочка — это неоплаченный счёт с прошедшим сроком. Оплаченный вчера
      // счёт с позавчерашним сроком уже никого не касается.
      тревога: (з) => пусто(з.paid_at) && срокПрошёл(з, 'due_at'),
    },
  ],

  группы: [
    { код: 'номер', заголовок: 'Номер и направление', вкладка: 'основное' },
    {
      код: 'кому',
      заголовок: 'Кому и за что',
      вкладка: 'основное',
      подсказка: 'Выберите сделку — стороны и договор подставятся из неё.',
    },
    { код: 'деньги', заголовок: 'Деньги', вкладка: 'основное' },
    {
      код: 'оплата',
      заголовок: 'Оплата',
      вкладка: 'основное',
      подсказка: 'Оплачен ли счёт, решает одна заполненная дата. Отдельной галочки нет: две приметы оплаты уже расходились между собой.',
    },
    { код: 'согласование', заголовок: 'Согласование', вкладка: 'связи' },
  ],

  поля: [
    {
      код: 'no',
      подпись: 'Номер',
      вид: 'строка',
      группа: 'номер',
      обязательно: выпущен,
      почемуОбязательно: 'по номеру счёт ищут в банке и в переписке; у черновика номера нет — он выдаётся при выпуске',
      максДлина: 64,
    },
    {
      код: 'direction',
      подпись: 'Направление',
      вид: 'список',
      варианты: НАПРАВЛЕНИЯ_СЧЁТА,
      группа: 'номер',
      обязательно: true,
      почемуОбязательно: 'выставленный нами и полученный от поставщика считаются в разные стороны',
      подсказка: 'Один тип с направлением, а не две несвязанные сущности: иначе один и тот же счёт заводят дважды.',
    },
    {
      код: 'issued_at',
      подпись: 'Дата выставления',
      вид: 'дата',
      группа: 'номер',
      обязательно: true,
      почемуОбязательно: 'от неё отсчитывается срок оплаты',
    },
    {
      код: 'deal_id',
      подпись: 'Сделка',
      вид: 'связь',
      связьС: 'сделка',
      группа: 'кому',
      подсказка: 'Необязательна: бывают счета без сделки — аренда, связь, хозяйственные расходы.',
    },
    {
      код: 'company_id',
      подпись: 'Контрагент',
      вид: 'связь',
      связьС: 'компания',
      группа: 'кому',
      обязательно: true,
      почемуОбязательно: 'счёт без контрагента не отнести ни к дебиторке, ни к кредиторке',
      подсказка: 'Подставляется из выбранной сделки.',
      подстановка: { из: 'deal_id', взять: 'company_id', толькоВПустое: true },
    },
    {
      код: 'own_company_id',
      подпись: 'Наша компания',
      вид: 'связь',
      связьС: 'компания',
      группа: 'кому',
      обязательно: true,
      почемуОбязательно: 'от какого нашего юрлица счёт — от него зависят реквизиты в бланке',
      подсказка: 'Подставляется из сделки. Юрлицо в счёте обязано совпадать с юрлицом договора.',
      подстановка: { из: 'deal_id', взять: 'own_company_id', толькоВПустое: true },
    },
    {
      код: 'contract_id',
      подпись: 'Договор',
      вид: 'связь',
      связьС: 'договор',
      группа: 'кому',
      подсказка: 'Подставляется из сделки. В бланке счёта печатается «по договору №…» — без ссылки этой строки не будет.',
      подстановка: { из: 'deal_id', взять: 'contract_id', толькоВПустое: true },
    },
    {
      код: 'amount',
      подпись: 'Сумма счёта',
      вид: 'деньги',
      группа: 'деньги',
      обязательно: true,
      почемуОбязательно: 'счёт без суммы оплатить нельзя',
      подсказка: 'Полная сумма с налогом. Сумма и валюта хранятся врозь — строку вида «515000|RUB» здесь не собрать.',
    },
    {
      код: 'vat_rate_id',
      подпись: 'Ставка НДС',
      вид: 'список',
      варианты: СТАВКИ_НДС,
      группа: 'деньги',
      подсказка: 'Ставка на дату выставления. Задним числом её менять нельзя — документ уже ушёл контрагенту.',
    },
    {
      код: 'vat_amount',
      подпись: 'В том числе НДС',
      вид: 'деньги',
      группа: 'деньги',
      подсказка: 'Часть суммы, а не добавка к ней. Печатается в бланке отдельной строкой.',
    },
    {
      код: 'expense_item_id',
      подпись: 'Статья расходов',
      вид: 'список',
      варианты: СТАТЬИ_РАСХОДОВ,
      группа: 'деньги',
      обязательно: (з) => строка(з, 'direction') === 'in',
      почемуОбязательно: 'входящий счёт без статьи не попадёт ни в одну строку расходов',
      подсказка: 'Только для счетов поставщиков. У выставленных нами статьи нет.',
    },
    {
      код: 'due_at',
      подпись: 'Оплатить до',
      вид: 'дата',
      группа: 'оплата',
      обязательно: true,
      почемуОбязательно: 'без срока счёт не попадёт ни в один список просроченных',
      подсказка: 'Срок из условий оплаты сделки, а не «через неделю на глаз».',
    },
    {
      код: 'paid_at',
      подпись: 'Дата оплаты',
      вид: 'дата',
      группа: 'оплата',
      подсказка: 'Единственная примета оплаты. Заполнена — счёт оплачен, пуста — нет.',
    },
    {
      код: 'paid_amount',
      подпись: 'Оплачено',
      вид: 'деньги',
      группа: 'оплата',
      подсказка: 'Если заплатили не всю сумму. Пусто при заполненной дате оплаты означает «оплачено полностью».',
    },
    {
      код: 'approved_by',
      подпись: 'Согласовал',
      вид: 'связь',
      связьС: 'сотрудник',
      группа: 'согласование',
      подсказка: 'Кто разрешил платить. Для входящих счетов без этого поля платёж не уходит.',
    },
    { код: 'approved_at', подпись: 'Дата согласования', вид: 'дата', группа: 'согласование' },
  ],

  обратные: [],

  действия: [
    {
      код: 'печать',
      подпись: 'Печать счёта',
      главное: true,
      почемуНельзя: (з) => {
        if (пусто(з.no)) return 'У черновика нет номера: сначала выпустите счёт'
        if (пусто(з.own_company_id)) return 'Не выбрана наша компания — в бланке нечего печатать в реквизитах'
        if (пусто(з.amount)) return 'Не названа сумма'
        return null
      },
    },
    {
      код: 'напомнить',
      подпись: 'Напомнить об оплате',
      почемуНельзя: (з) => {
        if (!пусто(з.paid_at)) return 'Счёт уже оплачен'
        if (пусто(з.due_at)) return 'Не назван срок оплаты — не о чем напоминать'
        return null
      },
    },
  ],
}

// ─────────────────────────── акт ───────────────────────────

export const АКТ: ОписаниеКарточки = {
  сущность: 'акт',
  один: 'акт',
  винительный: 'акт',
  много: 'Акты',
  документ: true,
  путь: (id) => `/cards/act/${id}`,
  заголовок: (з) => {
    const н = строка(з, 'no')
    return н === '' ? 'Акт без номера (черновик)' : `Акт № ${н}`
  },

  показатели: [
    {
      код: 'дата',
      подпись: 'Дата акта',
      значение: (з) => (пусто(з.act_date) ? '—' : fmtDate(строка(з, 'act_date'))),
    },
    {
      код: 'часы',
      подпись: 'Часы',
      значение: (з) => {
        const ч = число(з, 'hours_total')
        return ч === null ? '—' : String(ч)
      },
    },
    { код: 'сумма', подпись: 'Сумма', значение: (з) => суммаТекстом(з, 'amount') },
    {
      код: 'судно',
      подпись: 'Судно',
      значение: (з) => ярлык(з, 'vessel_id')?.название ?? '—',
    },
  ],

  группы: [
    { код: 'шапка', заголовок: 'Номер и дата', вкладка: 'основное' },
    {
      код: 'откуда',
      заголовок: 'По какой сделке',
      вкладка: 'основное',
      подсказка: 'Выберите сделку — заказчик, судно и инженер подставятся из неё.',
    },
    { код: 'работы', заголовок: 'Что сделано', вкладка: 'основное' },
    { код: 'деньги', заголовок: 'Деньги', вкладка: 'основное' },
    { код: 'вложения', заголовок: 'Сканы и фото', вкладка: 'документы' },
  ],

  поля: [
    {
      код: 'no',
      подпись: 'Номер акта',
      вид: 'строка',
      группа: 'шапка',
      обязательно: выпущен,
      почемуОбязательно: 'выпущенный акт без номера не сослаться в счёте; у черновика номера нет — он выдаётся при выпуске',
      максДлина: 64,
    },
    {
      код: 'act_date',
      подпись: 'Дата акта',
      вид: 'дата',
      группа: 'шапка',
      обязательно: true,
      почемуОбязательно: 'дата акта попадает в отчётный период — без неё выручку не отнести ни к какому месяцу',
    },
    {
      код: 'deal_id',
      подпись: 'Сделка',
      вид: 'связь',
      связьС: 'сделка',
      группа: 'откуда',
      обязательно: true,
      почемуОбязательно: 'акт всегда закрывает работы по сделке — иначе его не с чем сверить',
    },
    {
      код: 'company_id',
      подпись: 'Заказчик',
      вид: 'связь',
      связьС: 'компания',
      группа: 'откуда',
      обязательно: true,
      почемуОбязательно: 'акт подписывают две стороны, и одна из них — заказчик',
      подсказка: 'Подставляется из сделки.',
      подстановка: { из: 'deal_id', взять: 'company_id', толькоВПустое: true },
    },
    {
      код: 'vessel_id',
      подпись: 'Судно',
      вид: 'связь',
      связьС: 'судно',
      группа: 'откуда',
      подсказка: 'Подставляется из сделки. По этой ссылке акт виден в истории работ судна.',
      подстановка: { из: 'deal_id', взять: 'vessel_id', толькоВПустое: true },
    },
    {
      код: 'engineer_id',
      подпись: 'Инженер',
      вид: 'связь',
      связьС: 'сотрудник',
      группа: 'откуда',
      обязательно: true,
      почемуОбязательно: 'кто выполнял работы и подписывает акт со своей стороны',
      подсказка: 'Подставляется старший инженер сделки. Работал другой — поменяйте.',
      подстановка: { из: 'deal_id', взять: 'senior_engineer_id', толькоВПустое: true },
    },
    {
      код: 'place',
      подпись: 'Место работ',
      вид: 'строка',
      группа: 'работы',
      подсказкаВвода: 'Порт, причал, рейд',
      максДлина: 200,
    },
    {
      код: 'works',
      подпись: 'Перечень работ',
      вид: 'текст',
      группа: 'работы',
      подсказка: 'Что именно сделано, построчно. Печатается в акт дословно — «выполнены работы» заказчик не подпишет.',
    },
    {
      код: 'hours_total',
      подпись: 'Всего часов',
      вид: 'число',
      число: true,
      группа: 'работы',
      подсказка: 'Сумма часов по всем позициям. По ней сверяется загрузка инженера и стоимость работ.',
    },
    {
      код: 'conclusion',
      подпись: 'Заключение',
      вид: 'текст',
      группа: 'работы',
      подсказка: 'Состояние оборудования после работ и что осталось сделать. Это единственное место, где следующий инженер прочтёт, чего ждать.',
    },
    {
      код: 'amount',
      подпись: 'Сумма работ',
      вид: 'деньги',
      группа: 'деньги',
      подсказка: 'Может отличаться от суммы сделки: по одной сделке бывает несколько актов.',
    },
    {
      код: 'files',
      подпись: 'Вложения',
      вид: 'файл',
      группа: 'вложения',
      подсказка: 'Скан подписанного акта и фото с борта. У каждого вложения есть роль — по ней документ находят, а не по имени файла.',
    },
  ],

  обратные: [],
}

// ─────────────────────────── проверка ───────────────────────────

export const ПРОВЕРКА: ОписаниеКарточки = {
  сущность: 'проверка',
  один: 'проверка',
  винительный: 'проверку',
  много: 'Проверки',
  документ: true,
  путь: (id) => `/cards/inspection/${id}`,
  заголовок: (з) => {
    const вид = подписьВарианта(ВИДЫ_ПРОВЕРКИ, з, 'kind_id')
    const судно = ярлык(з, 'vessel_id')?.название
    return судно ? `${вид} — ${судно}` : вид
  },

  показатели: [
    {
      код: 'результат',
      подпись: 'Результат',
      значение: (з) => подписьВарианта(РЕЗУЛЬТАТЫ_ПРОВЕРКИ, з, 'result'),
      тревога: (з) => строка(з, 'result') !== 'ok' && !пусто(з.result),
    },
    {
      код: 'дефекты',
      подпись: 'Замечаний',
      значение: (з) => {
        const n = число(з, 'defects_found')
        return n === null ? '—' : String(n)
      },
      тревога: (з) => (число(з, 'defects_found') ?? 0) > 0,
    },
    {
      код: 'следующая',
      подпись: 'Следующая',
      значение: (з) => (пусто(з.next_due_at) ? '—' : fmtDate(строка(з, 'next_due_at'))),
      тревога: (з) => срокПрошёл(з, 'next_due_at'),
    },
  ],

  группы: [
    { код: 'что', заголовок: 'Что проверяли', вкладка: 'основное' },
    { код: 'кто', заголовок: 'Кто и когда', вкладка: 'основное' },
    {
      код: 'итог',
      заголовок: 'Результат',
      вкладка: 'основное',
      подсказка: 'Результат и срок следующей проверки. По сроку строится план работ на судне.',
    },
    { код: 'сверка', заголовок: 'Проверка результата', вкладка: 'связи' },
  ],

  поля: [
    {
      код: 'kind_id',
      подпись: 'Вид проверки',
      вид: 'список',
      варианты: ВИДЫ_ПРОВЕРКИ,
      группа: 'что',
      обязательно: true,
      почемуОбязательно: 'у каждого прибора свой срок следующей проверки, и считается он по виду',
    },
    {
      код: 'vessel_id',
      подпись: 'Судно',
      вид: 'связь',
      связьС: 'судно',
      группа: 'что',
      обязательно: true,
      почемуОбязательно: 'проверка всегда на конкретном судне — без него она ни в чей план не попадёт',
      подсказка: 'Подставляется из сделки, если сделка выбрана.',
      подстановка: { из: 'deal_id', взять: 'vessel_id', толькоВПустое: true },
    },
    {
      код: 'deal_id',
      подпись: 'Сделка',
      вид: 'связь',
      связьС: 'сделка',
      группа: 'что',
      подсказка: 'Необязательна: плановую проверку делают и без отдельной сделки.',
    },
    {
      код: 'engineer_id',
      подпись: 'Инженер',
      вид: 'связь',
      связьС: 'сотрудник',
      группа: 'кто',
      обязательно: true,
      почемуОбязательно: 'кто проверял — тот и отвечает за результат',
    },
    {
      код: 'performed_at',
      подпись: 'Когда проверяли',
      вид: 'дата',
      группа: 'кто',
      обязательно: true,
      почемуОбязательно: 'от этой даты отсчитывается срок следующей проверки',
    },
    {
      код: 'result',
      подпись: 'Результат',
      вид: 'список',
      варианты: РЕЗУЛЬТАТЫ_ПРОВЕРКИ,
      группа: 'итог',
      обязательно: true,
      почемуОбязательно: 'проверка без результата не проверка, а выезд',
    },
    {
      код: 'defects_found',
      подпись: 'Найдено замечаний',
      вид: 'число',
      число: true,
      группа: 'итог',
      обязательно: true,
      почемуОбязательно: 'по этому числу собирается сводка замечаний по флоту',
      подсказка: 'Ноль — это ответ «замечаний нет», а не «не считали». Пустое поле означало бы, что проверку не довели до конца.',
    },
    {
      код: 'next_due_at',
      подпись: 'Следующая проверка',
      вид: 'дата',
      группа: 'итог',
      подсказка: 'Считается от даты проверки по виду прибора. Прошедший срок подсвечивается: судно с просроченной проверкой в рейс не выпустят.',
    },
    {
      код: 'reviewed_by',
      подпись: 'Проверил результат',
      вид: 'связь',
      связьС: 'сотрудник',
      группа: 'сверка',
      подсказка: 'Второй человек, который сверил заключение. Не тот же, кто выполнял: сам себя не проверяют.',
    },
    { код: 'reviewed_at', подпись: 'Дата сверки', вид: 'дата', группа: 'сверка' },
  ],

  обратные: [],
}
